import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
} from "@mui/material";
import {
  ArrowUpward,
  ArrowDownward,
  Delete as DeleteIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { api } from "../../services/api";
import { NewsArticle } from "../../types";
import { formatDate } from "../../utils/dateFormat";

interface ArticleValidation {
  id: string;
  valid: boolean;
  reason?: string;
}

interface HomepageLayout {
  id?: string;
  pinnedArticleIds: string[];
  version: number;
  updatedBy: string;
  updatedAt: Date;
}

const ManageHomepageLayout: React.FC = () => {
  // Layout state
  const [pinnedArticles, setPinnedArticles] = useState<NewsArticle[]>([]);
  const [pinnedIds, setPinnedIds] = useState<string[]>([]);
  const [validation, setValidation] = useState<ArticleValidation[]>([]);

  // Available articles state
  const [availableArticles, setAvailableArticles] = useState<NewsArticle[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<NewsArticle | null>(null);

  // Load current layout
  const loadLayout = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await api.getHomepageLayout();

      if (result.success && result.layout) {
        const layout = result.layout as HomepageLayout;
        const validationData = result.validation as ArticleValidation[];

        setPinnedIds(layout.pinnedArticleIds || []);
        setValidation(validationData || []);

        // Fetch full article data for pinned articles
        if (layout.pinnedArticleIds && layout.pinnedArticleIds.length > 0) {
          // Fetch articles in parallel
          const articlePromises = layout.pinnedArticleIds.map((id) =>
            api.getPublishedArticle(id)
          );
          const articleResults = await Promise.all(articlePromises);

          // Filter successful results and preserve order
          const orderedArticles = articleResults
            .filter((result) => result.success && result.article)
            .map((result) => result.article as NewsArticle);

            setPinnedArticles(orderedArticles);
        }
      } else {
        // Empty layout
        setPinnedIds([]);
        setPinnedArticles([]);
        setValidation([]);
      }
    } catch (err: any) {
      setError(err.message || "Failed to load layout");
    } finally {
      setLoading(false);
    }
  };

  // Load available published articles
  const loadAvailableArticles = useCallback(async () => {
    try {
      const result = await api.searchNews({
        status: "published",
        orderBy: "publishedAt",
        orderDirection: "desc",
      });

      if (result.success && result.news) {
        // Filter out already pinned articles
        const pinnedSet = new Set(pinnedIds);
        const available = (result.news as NewsArticle[]).filter(
          article => !pinnedSet.has(article.id)
        );
        setAvailableArticles(available);
      }
    } catch (err: any) {
      console.error("Failed to load available articles:", err);
    }
  }, [pinnedIds]);

  useEffect(() => {
    loadLayout();
  }, []);

  useEffect(() => {
    loadAvailableArticles();
  }, [loadAvailableArticles]);

  // Move article up in order
  const handleMoveUp = (index: number) => {
    if (index === 0) return;

    const newArticles = [...pinnedArticles];
    [newArticles[index - 1], newArticles[index]] = [newArticles[index], newArticles[index - 1]];
    setPinnedArticles(newArticles);

    const newIds = newArticles.map(a => a.id);
    setPinnedIds(newIds);
  };

  // Move article down in order
  const handleMoveDown = (index: number) => {
    if (index === pinnedArticles.length - 1) return;

    const newArticles = [...pinnedArticles];
    [newArticles[index], newArticles[index + 1]] = [newArticles[index + 1], newArticles[index]];
    setPinnedArticles(newArticles);

    const newIds = newArticles.map(a => a.id);
    setPinnedIds(newIds);
  };

  // Remove article from pinned
  const handleRemove = (index: number) => {
    const newArticles = pinnedArticles.filter((_, i) => i !== index);
    setPinnedArticles(newArticles);

    const newIds = newArticles.map(a => a.id);
    setPinnedIds(newIds);
  };

  // Add article to pinned (at top)
  const handleAddArticle = (article: NewsArticle) => {
    setPinnedArticles([article, ...pinnedArticles]);
    setPinnedIds([article.id, ...pinnedIds]);
  };

  // Save layout
  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const result = await api.updateHomepageLayout({
        pinnedArticleIds: pinnedIds,
      });

      if (result.success) {
        setSuccess("Homepage layout saved successfully! Changes will appear on the homepage shortly.");
      } else {
        setError(result.error || "Failed to save layout");
      }
    } catch (err: any) {
      setError(err.message || "Failed to save layout");
    } finally {
      setSaving(false);
    }
  };

  // View article details
  const handleView = (article: NewsArticle) => {
    setSelectedArticle(article);
    setViewDialogOpen(true);
  };

  // Filter available articles by search term
  const filteredAvailable = availableArticles.filter(article =>
    article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    article.subtitle.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get validation status for an article
  const getValidationStatus = (articleId: string) => {
    return validation.find(v => v.id === articleId);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Manage Homepage Layout
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Drag articles to reorder them or use up/down buttons. Articles at the top will appear first on the homepage.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save Layout"}
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Pinned Articles */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Pinned Articles ({pinnedArticles.length})
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              These articles will appear first on the homepage in this order
            </Typography>

            {pinnedArticles.length === 0 ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                No articles pinned yet. Add articles from the right panel.
              </Alert>
            ) : (
              <List>
                {pinnedArticles.map((article, index) => {
                  const validationStatus = getValidationStatus(article.id);
                  const isInvalid = validationStatus && !validationStatus.valid;

                  return (
                    <ListItem
                      key={article.id}
                      sx={{
                        border: 1,
                        borderColor: isInvalid ? "error.main" : "divider",
                        borderRadius: 1,
                        mb: 1,
                        backgroundColor: isInvalid ? "error.lighter" : "background.paper",
                      }}
                      secondaryAction={
                        <Box>
                          <IconButton
                            edge="end"
                            onClick={() => handleMoveUp(index)}
                            disabled={index === 0}
                            size="small"
                          >
                            <ArrowUpward />
                          </IconButton>
                          <IconButton
                            edge="end"
                            onClick={() => handleMoveDown(index)}
                            disabled={index === pinnedArticles.length - 1}
                            size="small"
                          >
                            <ArrowDownward />
                          </IconButton>
                          <IconButton
                            edge="end"
                            onClick={() => handleView(article)}
                            size="small"
                          >
                            <ViewIcon />
                          </IconButton>
                          <IconButton
                            edge="end"
                            onClick={() => handleRemove(index)}
                            size="small"
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      }
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Chip label={`#${index + 1}`} size="small" color="primary" />
                            {isInvalid && <WarningIcon color="error" fontSize="small" />}
                            <Typography variant="body1">{article.title}</Typography>
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {article.subtitle}
                            </Typography>
                            {isInvalid && (
                              <Typography variant="caption" color="error">
                                {validationStatus?.reason}
                              </Typography>
                            )}
                            <Typography variant="caption" display="block">
                              Published: {formatDate(article.publishedAt)}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Available Articles */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Available Articles
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Search articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ mb: 2 }}
            />

            <List sx={{ maxHeight: 600, overflow: "auto" }}>
              {filteredAvailable.length === 0 ? (
                <Alert severity="info">
                  No available articles found
                </Alert>
              ) : (
                filteredAvailable.map((article) => (
                  <ListItem
                    key={article.id}
                    sx={{
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                      mb: 1,
                    }}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        onClick={() => handleAddArticle(article)}
                        color="primary"
                      >
                        <AddIcon />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={article.title}
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {article.subtitle}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Published: {formatDate(article.publishedAt)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))
              )}
            </List>
          </Paper>
        </Grid>
      </Grid>

      {/* View Article Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Article Preview
          <IconButton
            aria-label="close"
            onClick={() => setViewDialogOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedArticle && (
            <Box>
              <Typography variant="h5" gutterBottom>
                {selectedArticle.title}
              </Typography>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {selectedArticle.subtitle}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                {selectedArticle.content}
              </Typography>
              {selectedArticle.images && selectedArticle.images.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Images:
                  </Typography>
                  {selectedArticle.images.map((img, idx) => (
                    <img
                      key={idx}
                      src={img}
                      alt={`${selectedArticle.title} - ${idx + 1}`}
                      style={{ maxWidth: "100%", marginTop: 8 }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageHomepageLayout;
